import { useCallback, useEffect, useMemo, useState } from "react";
import { Star, Check, X, Trash2, RefreshCw, Search, Clock } from "lucide-react";

const STATUS_TABS = [
  { value: "pending", label: "На модерации" },
  { value: "approved", label: "Опубликованные" },
  { value: "rejected", label: "Отклонённые" },
  { value: "", label: "Все" },
];

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

const Stars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= value ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`} />
    ))}
  </div>
);

export function ReviewsTab({ password }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [counts, setCounts] = useState({ pending: 0, approved: 0 });
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/reviews?password=${encodeURIComponent(password)}&status=${statusFilter}&limit=500`);
      const d = await r.json();
      setReviews(d.reviews || []);
      setCounts({ pending: d.pending_count || 0, approved: d.approved_count || 0 });
    } catch { /* keep prior */ }
    setLoading(false);
  }, [password, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reviews;
    return reviews.filter((r) => `${r.name} ${r.city} ${r.text}`.toLowerCase().includes(q));
  }, [reviews, search]);

  const setStatus = async (r, status) => {
    const res = await fetch(`/api/admin/reviews/${r.id}?password=${encodeURIComponent(password)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Удалить отзыв от ${r.name}? Это действие нельзя отменить.`)) return;
    const res = await fetch(`/api/admin/reviews/${r.id}?password=${encodeURIComponent(password)}`, { method: "DELETE" });
    if (res.ok) setReviews((prev) => prev.filter((x) => x.id !== r.id));
  };

  const removePhoto = async (r) => {
    if (!window.confirm(`Удалить фото из отзыва ${r.name}? Текст отзыва останется.`)) return;
    const res = await fetch(`/api/admin/reviews/${r.id}/photo?password=${encodeURIComponent(password)}`, { method: "DELETE" });
    if (res.ok) setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, photo_url: "" } : x)));
  };

  return (
    <div className="space-y-4" data-testid="reviews-tab">
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((s) => (
            <button
              key={s.value || "all"}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 text-sm rounded-full font-medium ${statusFilter === s.value ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              data-testid={`reviews-filter-${s.value || "all"}`}
            >
              {s.label}
              {s.value === "pending" && counts.pending > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-amber-400 text-amber-950">{counts.pending}</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 lg:max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по имени, городу, тексту…"
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="reviews-search" />
          </div>
          <button onClick={load} disabled={loading} className="p-2 text-gray-500 hover:text-pink-500 hover:bg-pink-50 rounded-lg" data-testid="reviews-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-gray-500" data-testid="reviews-empty">
          {loading ? "Загружаем отзывы…" : "Отзывов пока нет."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start gap-3">
                {r.photo_url && (
                  <div className="shrink-0 flex flex-col items-center gap-1">
                    <a href={r.photo_url} target="_blank" rel="noreferrer" data-testid={`review-photo-link-${r.id}`}>
                      <img src={r.photo_url} alt={r.name} className="w-16 h-16 rounded-lg object-cover" />
                    </a>
                    <button
                      type="button"
                      onClick={() => removePhoto(r)}
                      className="text-[11px] text-red-600 hover:text-red-700 hover:underline"
                      data-testid={`review-photo-delete-${r.id}`}
                    >
                      Удалить фото
                    </button>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{r.name}</span>
                    <Stars value={r.rating} />
                    <span className="text-xs text-gray-400">📍 {r.city || "—"}</span>
                    <span className="text-[10px] uppercase font-bold text-gray-400">{r.lang}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTs(r.ts)}</span>
                    {r.status && (
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>
                        {r.status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{r.text}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                {r.status !== "approved" && (
                  <button onClick={() => setStatus(r, "approved")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition"
                    data-testid={`review-approve-${r.id}`}>
                    <Check className="w-3.5 h-3.5" /> Опубликовать
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => setStatus(r, "rejected")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition"
                    data-testid={`review-reject-${r.id}`}>
                    <X className="w-3.5 h-3.5" /> Отклонить
                  </button>
                )}
                <button onClick={() => remove(r)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition"
                  data-testid={`review-delete-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReviewsTab;
